import React, { useRef, useState } from 'react';
import { Icon } from './Icon';
import '../../../styles/components/file-input.css';
import { SecurityUtils } from '../../../utils/security';

// Types de fichiers autorisés
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg', 
  'image/png',
  'image/webp',
  'image/gif'
];

// Taille maximale par défaut (5MB)
const DEFAULT_MAX_SIZE = 5 * 1024 * 1024;

// Types de fichiers dangereux à bloquer
const DANGEROUS_EXTENSIONS = [
  '.exe', '.bat', '.cmd', '.com', '.pif', '.scr', '.vbs', '.js', '.jar',
  '.php', '.asp', '.aspx', '.jsp', '.py', '.rb', '.pl', '.sh', '.ps1'
];

export interface FileInputProps {
  // Props essentielles
  id: string;
  name: string;
  onChange: (files: FileList | null) => void;
  
  // Props optionnelles
  label?: string;
  helper?: string;
  error?: string;
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // en bytes
  required?: boolean;
  disabled?: boolean;
  className?: string;
  dragAndDrop?: boolean;
  // Nouvelles props pour la galerie
  previewFiles?: string[];
  onFileRemove?: (index: number) => void;
  previewType?: 'names' | 'thumbnails';
  // Nouvelles props de sécurité
  enableSecurityValidation?: boolean;
  allowedTypes?: string[];
  maxFiles?: number;
}

export const FileInput: React.FC<FileInputProps> = ({
  id,
  name,
  onChange,
  label,
  helper,
  error,
  accept,
  multiple = false,
  maxSize = DEFAULT_MAX_SIZE,
  required = false,
  disabled = false,
  className = '',
  dragAndDrop = true,
  // Nouvelles props avec valeurs par défaut
  previewFiles = [],
  onFileRemove,
  previewType = 'thumbnails',
  // Props de sécurité
  enableSecurityValidation = true,
  allowedTypes = ALLOWED_IMAGE_TYPES,
  maxFiles = 10,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [validationError, setValidationError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const containerClasses = [
    'file-input-container',
    dragAndDrop ? 'drag-drop-enabled' : '',
    isDragging ? 'is-dragging' : '',
    error ? 'has-error' : '',
    disabled ? 'is-disabled' : '',
    className
  ].filter(Boolean).join(' ');

  // 🔒 VALIDATION DE SÉCURITÉ DES FICHIERS
  const validateFileSecurity = async (file: File): Promise<{ isValid: boolean; error?: string }> => {
    // 1. Vérification de la taille
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `File too large. Maximum size: ${(maxSize / 1024 / 1024).toFixed(1)}MB`
      };
    }

    // 2. Vérification du type MIME
    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: `File type not allowed. Allowed types: ${allowedTypes.join(', ')}`
      };
    }

    // 3. Vérification de l'extension (double vérification)
    const fileName = file.name.toLowerCase();
    const hasDangerousExtension = DANGEROUS_EXTENSIONS.some(ext => 
      fileName.endsWith(ext)
    );
    
    if (hasDangerousExtension) {
      return {
        isValid: false,
        error: 'Dangerous file type detected'
      };
    }

    // 4. Vérification du nom de fichier (éviter les noms malveillants)
    if (!SecurityUtils.validateFileName(file.name)) {
      return {
        isValid: false,
        error: 'Invalid file name'
      };
    }

    // 5. Vérification que c'est bien une image (pour les images)
    if (file.type.startsWith('image/')) {
      return new Promise<{ isValid: boolean; error?: string }>((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve({ isValid: true });
        };
        
        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve({
            isValid: false,
            error: 'Invalid image file'
          });
        };
        
        img.src = url;
      });
    }

    return { isValid: true };
  };

  //  VALIDATION EN LOT
  const validateFiles = async (files: FileList): Promise<{ 
    validFiles: File[]; 
    errors: string[] 
  }> => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    // Vérifier le nombre de fichiers
    if (files.length > maxFiles) {
      errors.push(`Too many files. Maximum: ${maxFiles}`);
      return { validFiles, errors };
    }

    // Valider chaque fichier
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const validation = await validateFileSecurity(file);
      
      if (validation.isValid) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: ${validation.error}`);
      }
    }

    return { validFiles, errors };
  };

  const handleFileChange = async (files: FileList | null) => {
    if (!files || disabled) return;

    setValidationError('');

    if (enableSecurityValidation) {
      // 🔒 VALIDATION DE SÉCURITÉ
      const { validFiles, errors } = await validateFiles(files);
      
      if (errors.length > 0) {
        setValidationError(errors.join(', '));
        // Ne pas accepter de fichiers si il y a des erreurs
        if (validFiles.length === 0) {
          setSelectedFiles(null);
          onChange(null);
          return;
        }
        // Si certains fichiers sont valides, on les accepte
        const dataTransfer = new DataTransfer();
        validFiles.forEach(file => dataTransfer.items.add(file));
        files = dataTransfer.files;
      }
    }

    setSelectedFiles(files);
    onChange(files);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileChange(e.target.files);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && dragAndDrop) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!disabled && dragAndDrop) {
      handleFileChange(e.dataTransfer.files);
    }
  };

  const getFileNames = () => {
    if (!selectedFiles) return '';
    return Array.from(selectedFiles)
      .map(file => file.name)
      .join(', ');
  };

  return (
    <div className="file-input-wrapper">
      {label && (
        <label htmlFor={id} className="file-input-label">
          {label}
          {required && <span className="required-mark">*</span>}
        </label>
      )}
      
      <div
        className={containerClasses}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        role="button"
        tabIndex={disabled ? -1 : 0}
      >
        <input
          ref={fileInputRef}
          id={id}
          name={name}
          type="file"
          accept={accept}
          multiple={multiple}
          required={required}
          disabled={disabled}
          className="file-input-field"
          onChange={handleInputChange}
          aria-invalid={!!error}
          aria-describedby={helper ? `${id}-helper` : undefined}
          style={{ 
            position: 'absolute',
            opacity: 0,
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            cursor: 'pointer',
          }}
        />

        <div className="file-input-content">
          <Icon 
            name="Upload" 
            size={24} 
            className="file-input-icon"
            aria-hidden="true"
          />
          
          <div className="file-input-text">
            {selectedFiles ? (
              <>
                <span className="selected-files">{getFileNames()}</span>
                {multiple && (
                  <span className="secondary-text add-more-text">Click to add more photos</span>
                )}
              </>
            ) : (
              <>
                <span className="primary-text">
                  {dragAndDrop ? 'Drag and drop your files here or' : 'Choose files'}
                </span>
                <span className="secondary-text">Browse</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* File list - below the drop zone */}
      {previewFiles.length > 0 && (
        <div className="file-input-file-list">
          {previewFiles.map((file, index) => (
            <div key={index} className="file-input-file-list__item">
              <Icon name="FileText" size={16} className="file-input-file-list__icon" />
              <span className="file-input-file-list__name">
                {previewType === 'thumbnails'
                  ? file.split('/').pop()?.split('?')[0] || `File ${index + 1}`
                  : file.split('/').pop()?.split('?')[0] || `File ${index + 1}`
                }
              </span>
              {onFileRemove && (
                <button
                  type="button"
                  className="file-input-file-list__remove"
                  onClick={() => onFileRemove(index)}
                  aria-label={`Remove ${file.split('/').pop()?.split('?')[0] || `file ${index + 1}`}`}
                >
                  <Icon name="X" size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Affichage des erreurs de validation */}
      {validationError && (
        <p className="file-input-security-error" role="alert">
          🔒 Security: {validationError}
        </p>
      )}

      {helper && !error && (
        <p id={`${id}-helper`} className="file-input-helper">
          {helper}
        </p>
      )}
      
      {error && (
        <p id={`${id}-error`} className="file-input-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default FileInput; 